import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import { getQueryStringValue } from '../../../common/helpers';
import domains from '../../../ols/constants/domains';
import goPayDomains from '../../../constants/gopay';
import CenteredNotice from '../../../common/CenteredNotice';
/*
 *  This component exists to enable cross-domain authentication for GoPay domains.
 *  Due to cookie security issues, the browser needs to redirect to the GoPay domain to set
 *  cookies there. OLS GoPay conditionally redirects back to the website domain or to the
 *  checkout form based on an `app` parameter.
 */

const styles = {
  loader: {
    color: 'primary'
  }
};

const VALID_ACTIONS = ['signin', 'signout'];
const VALID_APPS = ['ols', 'website'];

class Redirect extends React.Component {
  state = {
    redirect: getQueryStringValue('r') || '/',
    nonce: getQueryStringValue('nonce'),
    app: decodeURIComponent(getQueryStringValue('app')),
    action: decodeURIComponent(getQueryStringValue('action'))
  };

  componentDidMount() {
    this.redirect();
  }

  get olsBaseUrl() {
    const { websiteId, env } = this.props;
    const domain = domains[env] || domains.production;
    return `https://${websiteId}.${domain}`;
  }

  get goPayBaseUrl() {
    const { websiteId, env } = this.props;
    const domain = goPayDomains[env] || goPayDomains.production;
    return `https://${domain}/api/websites/${websiteId}`;
  }

  get validatedApp() {
    const { app } = this.state;
    return VALID_APPS.includes(app) ? app : 'website';
  }

  get validatedAction() {
    const { action } = this.state;
    return VALID_ACTIONS.includes(action) ? action : 'signout';
  }

  get isSignOut() {
    return this.validatedAction === 'signout';
  }

  get olsAuthUrl() {
    const { nonce, redirect } = this.state;
    const { olsBaseUrl, validatedAction, validatedApp, isSignOut } = this;

    let params = `?r=${redirect}&app=${validatedApp}`;
    params = isSignOut ? params : `${params}&nonce=${nonce}`;
    return `${olsBaseUrl}/api/v1/membership/${validatedAction}${params}`;
  }

  get goPayAuthUrl() {
    const { nonce, redirect } = this.state;
    const { hasOls, hasOla } = this.props;
    const { goPayBaseUrl, validatedAction, validatedApp, isSignOut } = this;

    let params = `?r=${redirect}&app=${validatedApp}`;

    if (!isSignOut) {
      params += `&nonce=${nonce}`;
    }
    if (hasOla) {
      params += '&sso=ola';
    }
    if (hasOls) {
      params += '&sso=ols';
    }

    return `${goPayBaseUrl}/${validatedAction}${params}`;
  }

  get goPayRedirectUrl() {
    const { redirect } = this.state;
    const { hasOls, hasOla } = this.props;
    const { goPayAuthUrl } = this;
    return hasOls || hasOla ? goPayAuthUrl : redirect;
  }

  get redirectUrl() {
    const { redirect } = this.state;
    const { hasOls, goPay } = this.props;
    const { olsAuthUrl, goPayRedirectUrl } = this;

    if (goPay) {
      return goPayRedirectUrl;
    }
    if (hasOls) {
      return olsAuthUrl;
    }

    return decodeURIComponent(redirect);
  }

  redirect = () => {
    window.location.assign(this.redirectUrl);
  };

  render() {
    const { Loader, Block } = UX2.Element;

    return (
      <CenteredNotice
        body={
          <Block style={ styles.loader }>
            <Loader />
          </Block>
        }
      />
    );
  }
}

Redirect.propTypes = {
  env: PropTypes.string.isRequired,
  websiteId: PropTypes.string.isRequired,
  staticContent: PropTypes.object.isRequired,
  hasOls: PropTypes.bool,
  hasOla: PropTypes.bool,
  goPay: PropTypes.bool
};

export default Redirect;
